import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Paper
} from '@mui/material';
import { Home as HomeIcon, Sports as SportsIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import OddsList from '../OddsList';
import { OddsListSkeleton } from '../SkeletonLoaders';
import BackButton from '../BackButton';

// Static sports list in the specified order
const STATIC_SPORTS = [
  { key: 'americanfootball_nfl', title: 'NFL' },
  { key: 'basketball_nba', title: 'NBA' },
  { key: 'basketball_ncaab', title: 'NCAAB' },
  { key: 'mma_mixed_martial_arts', title: 'MMA' },
  { key: 'baseball_mlb', title: 'MLB' },
  { key: 'basketball_wnba', title: 'WNBA' },
  { key: 'soccer_usa_mls', title: 'MLS' },
  { key: 'americanfootball_ncaaf', title: 'NCAAF' }
];

const MainOddsScreen = ({ odds, loading, error, onLoadOdds }) => {
  const [selectedSport, setSelectedSport] = useState('all');

  // Auto-load all odds when component mounts
  useEffect(() => {
    if (onLoadOdds && (!odds || odds.length === 0) && !loading) {
      onLoadOdds('upcoming');
    }
  }, [onLoadOdds]);

  const handleSportFilter = (sportKey) => {
    setSelectedSport(sportKey);
    // Call onLoadOdds with the selected sport
    if (onLoadOdds) {
      onLoadOdds(sportKey === 'all' ? 'upcoming' : sportKey);
    }
  };

  const handleRefresh = async () => {
    // Refresh odds data
    if (onLoadOdds) {
      await onLoadOdds(selectedSport === 'all' ? 'upcoming' : selectedSport);
    }
  };

  const filteredOdds = selectedSport === 'all'
    ? odds
    : odds?.filter(game => game.sport_key === selectedSport);

  return (
    <Box>
      <BackButton />
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
            All Odds
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Compare betting odds across all sports and sportsbooks
          </Typography>
        </Box>
        {filteredOdds && filteredOdds.length > 0 && (
          <Chip 
            label={`${filteredOdds.length} games`} 
            size="small" 
            variant="outlined" 
          />
        )}
      </Box>

      {/* Sports Filter */}
      <Box 
        sx={{ 
          mb: 3,
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: 6,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: 3,
          },
        }}
      >
        <Stack 
          direction="row" 
          spacing={1} 
          sx={{ 
            minWidth: 'max-content',
            pb: 1
          }}
        >
          <Chip
            label="All Sports"
            onClick={() => handleSportFilter('all')}
            color={selectedSport === 'all' ? 'primary' : 'default'}
            variant={selectedSport === 'all' ? 'filled' : 'outlined'}
            sx={{ cursor: 'pointer' }}
          />
          {STATIC_SPORTS.map((sport) => (
            <Chip
              key={sport.key}
              label={sport.title}
              onClick={() => handleSportFilter(sport.key)}
              color={selectedSport === sport.key ? 'primary' : 'default'}
              variant={selectedSport === sport.key ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          ))}
          {onLoadOdds && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
              sx={{ ml: 1, whiteSpace: 'nowrap' }}
            >
              Refresh
            </Button>
          )}
        </Stack>
      </Box>

      {/* Hero Banner - HIDDEN */}
      {/* <Box
        sx={{
          position: 'relative',
          height: { xs: 250, sm: 280, md: 300 },
          mb: 3,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/assets/promo1.jfif"
          alt="Promotion"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            p: 3,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: 'white',
              fontWeight: 700,
              textAlign: 'center',
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
            }}
          >
            Welcome to Live-betting Bonus
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 500,
              textAlign: 'center',
              fontSize: { xs: '1.1rem', sm: '1.5rem', md: '1.75rem' },
              textShadow: '1px 1px 3px rgba(0,0,0,0.5)',
            }}
          >
            Compare Odds & Win Big
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              bgcolor: 'secondary.main',
              color: 'white',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              mt: 1,
              '&:hover': {
                bgcolor: '#22c55e',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(74, 222, 128, 0.4)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Get Started
          </Button>
        </Box>
      </Box> */}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

      {/* Content Area */}
      {loading ? (
        <OddsListSkeleton count={4} />
      ) : filteredOdds && filteredOdds.length > 0 ? (
        <OddsList
          odds={filteredOdds}
          selectedSport={selectedSport !== 'all' ? STATIC_SPORTS.find(s => s.key === selectedSport) : null}
          isHomeScreen={true}
          maxGames={10}
        />
      ) : odds && odds.length > 0 && filteredOdds?.length === 0 ? (
        <Card sx={{ mt: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No games found for {STATIC_SPORTS.find(s => s.key === selectedSport)?.title || 'selected sport'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try selecting a different sport or "All Sports" to see available games
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mt: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Ready to compare odds?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a sport and click "Load Odds" to see the latest betting lines from top sportsbooks
            </Typography>
          </CardContent>
        </Card>
      )}
      </Box>
  );
};

export default MainOddsScreen;
